import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import Button from "@/components/base/Button";
import { clinicInfo } from "@/mocks/site";

interface Crumb {
  label: string;
  to?: string;
}

interface CareHeroProps {
  eyebrow: string;
  title: ReactNode;
  intro: string;
  image: string;
  imageAlt: string;
  badgeIcon?: string;
  badgeTitle?: string;
  badgeText?: string;
  chips?: string[];
  chipsLabel?: string;
  crumbs: Crumb[];
}

/**
 * Premium full-width hero for the specialist care landing pages (Spine Care,
 * Orthopaedic Care). Image-driven composition with a layered frame, floating
 * credential card and a factual trust strip.
 */
export default function CareHero({
  eyebrow,
  title,
  intro,
  image,
  imageAlt,
  badgeIcon = "ri-shield-check-line",
  badgeTitle = "Senior Consultant",
  badgeText = "Orthopaedic & Spine Surgery",
  chips = [],
  chipsLabel = "Areas we treat",
  crumbs,
}: CareHeroProps) {
  return (
    <section className="relative overflow-hidden bg-background-50">
      <div
        className="absolute inset-0 bg-gradient-to-b from-background-50 via-background-50 to-background-100"
        aria-hidden="true"
      ></div>
      <div
        className="absolute inset-0 opacity-[0.35] [background-image:linear-gradient(to_right,oklch(var(--background-200))_1px,transparent_1px),linear-gradient(to_bottom,oklch(var(--background-200))_1px,transparent_1px)] [background-size:72px_72px]"
        aria-hidden="true"
      ></div>
      <div
        className="absolute -right-24 -top-20 h-[30rem] w-[30rem] rounded-full bg-accent-100/60 blur-3xl"
        aria-hidden="true"
      ></div>
      <div
        className="absolute -bottom-40 -left-24 h-[26rem] w-[26rem] rounded-full bg-primary-100/60 blur-3xl"
        aria-hidden="true"
      ></div>
      <div
        className="absolute left-1/3 top-1/3 h-72 w-72 rounded-full bg-secondary-200/30 blur-3xl"
        aria-hidden="true"
      ></div>

      <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-10 md:px-6 md:pt-12">
        <nav aria-label="Breadcrumb">
          <ol className="flex flex-wrap items-center gap-2 font-label text-xs text-foreground-500">
            {crumbs.map((crumb, index) => (
              <li key={crumb.label} className="flex items-center gap-2">
                {crumb.to ? (
                  <Link to={crumb.to} className="transition-colors hover:text-primary-700">
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-foreground-800">{crumb.label}</span>
                )}
                {index < crumbs.length - 1 ? (
                  <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true"></i>
                ) : null}
              </li>
            ))}
          </ol>
        </nav>

        <div className="mt-8 grid grid-cols-1 items-center gap-x-12 gap-y-14 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <span className="animate-fade-up inline-flex items-center gap-2 rounded-full border border-accent-200 bg-accent-50 px-3.5 py-1.5 font-label text-xs font-semibold uppercase tracking-[0.16em] text-accent-700">
              <span className="h-1.5 w-1.5 rounded-full bg-accent-500" aria-hidden="true"></span>
              {eyebrow}
            </span>

            <h1 className="animate-fade-up mt-6 font-heading text-[2.4rem] font-semibold leading-[1.05] tracking-tight text-foreground-950 md:text-5xl lg:text-[3.4rem]">
              {title}
            </h1>

            <p className="animate-fade-up mt-6 max-w-xl font-body text-base leading-relaxed text-foreground-600 md:text-lg">
              {intro}
            </p>

            <div className="animate-fade-up mt-8 flex flex-col gap-3 sm:flex-row">
              <Button to="/appointment" size="lg" icon="ri-calendar-check-line">
                Book an Appointment
              </Button>
              <Button href={clinicInfo.phoneHref} variant="outline" size="lg" icon="ri-phone-fill">
                Call Now
              </Button>
            </div>

            {chips.length ? (
              <div className="animate-fade-up mt-9">
                <p className="font-label text-xs font-semibold uppercase tracking-[0.16em] text-foreground-500">
                  {chipsLabel}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {chips.map((chip) => (
                    <span
                      key={chip}
                      className="rounded-full border border-background-300 bg-background-50 px-3 py-1.5 font-label text-xs font-medium text-foreground-700"
                    >
                      {chip}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <div className="lg:col-span-6">
            <div className="relative mx-auto w-full max-w-[460px]">
              <div
                className="absolute -inset-x-5 -bottom-6 top-10 rounded-[2.5rem] bg-accent-100/70"
                aria-hidden="true"
              ></div>
              <div
                className="absolute -right-3 -top-6 h-24 w-24 rounded-full border border-accent-200 bg-accent-50/50"
                aria-hidden="true"
              ></div>
              <div className="absolute -left-5 bottom-28 h-16 w-16 rounded-full bg-primary-100" aria-hidden="true"></div>

              <div className="relative overflow-hidden rounded-[2.5rem] border border-background-200 bg-background-100 shadow-[0_35px_80px_-45px_rgba(15,40,80,0.5)]">
                <div className="h-[420px] w-full md:h-[540px]">
                  <img
                    src={image}
                    alt={imageAlt}
                    title={imageAlt}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-primary-950/45 to-transparent" aria-hidden="true"></div>
              </div>

              <div className="absolute -bottom-6 -left-2 flex w-[15.5rem] items-center gap-3 rounded-2xl border border-background-200 bg-background-50/95 p-3 shadow-[0_20px_45px_-28px_rgba(15,40,80,0.55)] backdrop-blur md:-left-8">
                <span className="flex h-11 w-11 flex-none items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className={`${badgeIcon} text-xl`} aria-hidden="true"></i>
                </span>
                <span className="min-w-0">
                  <span className="block font-heading text-sm font-semibold text-foreground-950">{badgeTitle}</span>
                  <span className="block font-label text-xs text-foreground-600">{badgeText}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}